import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Search } from 'lucide-react';
import { BellIcon } from '../../components/BellIcon';
import { HostBottomNav } from '../../components/HostBottomNav';
import { MessengerListShell } from '../../components/messaging/MessengerListShell';
import { MessengerListRow } from '../../components/messaging/MessengerListRow';
import { formatChatDisplayName } from '../../utils/nameFormatters';
import { buildMessageHeaders } from '../../utils/messageHeaders';
import { primeCallPermissions } from '../../utils/callPermissions';
import { SupportRowBadge } from '../../components/messaging/SupportSystemCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HostMessagesPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    // Prompt for microphone permission the FIRST time a host or guest opens
    // their messages tab so the OS dialog fires ahead of the actual phone
    // button — otherwise the getUserMedia call inside CallContext races the
    // gesture window on Capacitor Android and silently fails with the
    // generic "Unable to start the call" toast.
    primeCallPermissions('host');
    const fetchConversations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/messages/conversations`, {
          headers: buildMessageHeaders(token, null, 'host'),
        });
        setConversations(response.data || []);
      } catch (error) {
        console.error('Failed to load host conversations', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, []);

  const filteredConversations = useMemo(() => conversations.filter((conversation) => {
    const name = conversation.other_user?.name || conversation.other_user?.username || '';
    const matchesSearch = !search || name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || (filter === 'support' && conversation.type === 'support') || (filter === 'guest' && conversation.type !== 'support');
    return matchesSearch && matchesFilter;
  }), [conversations, filter, search]);

  const filterLabel = filter === 'all' ? 'All messages' : filter === 'support' ? 'Support' : 'Guests';

  const filterNode = (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
      <button
        onClick={() => setFilterOpen((prev) => !prev)}
        data-testid="host-messages-filter-button"
        className="premium-card-compact premium-card-nested"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', cursor: 'pointer' }}
      >
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text)' }}>{filterLabel}</span>
        <ChevronDown size={16} color="var(--text-2)" style={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
      </button>
      {filterOpen ? (
        <div className="premium-card-compact premium-card-nested" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, minWidth: 150, marginTop: 6, overflow: 'hidden' }}>
          {[
            ['all', 'All messages'],
            ['guest', 'Guests'],
            ['support', 'Support'],
          ].map(([value, label]) => (
            <button
              key={value}
              data-testid={`host-messages-filter-${value}`}
              onClick={() => { setFilter(value); setFilterOpen(false); }}
              style={{ width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 15, color: filter === value ? 'var(--accent-purple)' : '#666' }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <MessengerListShell
      pageTestId="host-messages-page"
      bellNode={<button data-testid="host-messages-bell-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><BellIcon /></button>}
      bottomNav={<HostBottomNav />}
      title="Messages"
      searchValue={search}
      onSearchChange={(event) => setSearch(event.target.value)}
      searchPlaceholder="Guest name"
      searchTestId="host-messages-search-input"
      searchIcon={<Search size={18} color="#8A8797" />}
      cardHeader={filterNode}
    >
      {loading ? <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-3)', textAlign: 'center', paddingTop: 20 }}>Loading conversations...</p> : null}
      {!loading && filteredConversations.length === 0 ? <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-3)', textAlign: 'center', paddingTop: 20 }}>No messages yet</p> : null}

      {filteredConversations.map((conversation) => {
        const isSupport = conversation.type === 'support';
        const guestName = isSupport ? 'Allure' : (formatChatDisplayName(conversation.other_user) || 'Guest');
        return (
          <MessengerListRow
            key={conversation.id}
            name={guestName}
            preview={conversation.last_message || 'Open conversation'}
            imageUrl={isSupport ? '/notification-icon-192.png' : (conversation.other_user?.image_url || conversation.other_user?.photo_url)}
            unreadCount={conversation.unread_count || 0}
            timestamp={conversation.last_message_at}
            previewTone={conversation.last_message_kind}
            rowBadge={isSupport ? (
              <SupportRowBadge
                status={conversation.support_status}
                dataTestId={`host-message-conversation-${conversation.id}-support-badge`}
              />
            ) : null}
            dataTestId={`host-message-conversation-${conversation.id}`}
            onClick={() => navigate(`/host/chat/${conversation.id}`)}
          />
        );
      })}
    </MessengerListShell>
  );
}